import { useState } from 'react';
import { useAuth } from '../lib/auth';
import { usePlan } from '../lib/billing';
import type { ArubaIntegration, NetworkDeviceKind } from '../lib/types';
import { DataSourcePicker, useIntegrationMutations } from './integration-shell';
import { Instructions, RemoveCredentials, Step, useCredentials } from './mdm-integrations';
import { Alert, Button, Field, Input, Switch } from './ui';

/** The kinds a school chooses to import. Central reports these three. */
const KINDS: { value: NetworkDeviceKind; label: string; hint: string }[] = [
  { value: 'ACCESS_POINT', label: 'Access points', hint: 'Also what the BSSID list is built from' },
  { value: 'SWITCH', label: 'Switches', hint: '' },
  { value: 'GATEWAY', label: 'Gateways', hint: '' },
];

export function ArubaConnection({ integration }: { integration: ArubaIntegration }) {
  const { can } = useAuth();
  const isAdmin = can('ADMIN');
  const { save, remove } = useCredentials('ARUBA_CENTRAL');
  const { update } = useIntegrationMutations('ARUBA_CENTRAL');
  const saved = integration.aruba;
  const [host, setHost] = useState(integration.settings.host ?? '');
  const [clientId, setClientId] = useState(saved.clientId ?? '');
  const [clientSecret, setClientSecret] = useState('');

  return (
    <div className="space-y-8">
      <section>
        <h3 className="mb-3 text-sm font-semibold text-slate-900">Data source</h3>
        <DataSourcePicker integration={integration} />
      </section>

      {integration.mode === 'LIVE' && (
        <>
          <Step number={1} title="API client">
            <Instructions
              steps={[
                <>
                  In <strong>HPE GreenLake</strong>, open <strong>Manage Account › Application &amp; API</strong> and create an API client for Central. Copy its{' '}
                  <strong>client ID</strong> and <strong>client secret</strong> - the secret is shown once.
                </>,
                <>
                  Give the client read access to network monitoring. Edvora only reads: it never changes anything in Central.
                </>,
                <>
                  In Central, open <strong>Menu › API Gateway › REST API</strong> and copy the <strong>Base URL</strong> for your region, for example{' '}
                  <code>us1.api.central.arubanetworks.com</code>. A different region’s host won’t answer.
                </>,
              ]}
            />
            {saved.secretsUnreadable && <Alert tone="red">The saved credentials can’t be read. Enter them again.</Alert>}
            <div className="grid max-w-2xl gap-4 sm:grid-cols-2">
              <Field label="Central API host" className="sm:col-span-2" hint="From Central › API Gateway › REST API">
                <Input
                  value={host}
                  onChange={(e) => setHost(e.target.value)}
                  disabled={!isAdmin}
                  placeholder="us1.api.central.arubanetworks.com"
                  className="font-mono text-xs"
                />
              </Field>
              <Field label="Client ID">
                <Input value={clientId} onChange={(e) => setClientId(e.target.value)} disabled={!isAdmin} className="font-mono text-xs" />
              </Field>
              <Field label="Client secret">
                <Input
                  type="password"
                  value={clientSecret}
                  onChange={(e) => setClientSecret(e.target.value)}
                  disabled={!isAdmin}
                  placeholder={saved.hasClientSecret ? 'Saved - leave blank to keep' : ''}
                  className="font-mono text-xs"
                />
              </Field>
            </div>
            {isAdmin && (
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="primary"
                  loading={save.isPending || update.isPending}
                  onClick={async () => {
                    const trimmed = host.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/.*$/, '');
                    if (trimmed !== (integration.settings.host ?? '')) await update.mutateAsync({ settings: { host: trimmed } });
                    setHost(trimmed);
                    save.mutate({ clientId, ...(clientSecret ? { clientSecret } : {}) }, { onSuccess: () => setClientSecret('') });
                  }}
                >
                  Save API access
                </Button>
                {(saved.clientId || saved.hasClientSecret) && <RemoveCredentials provider="ARUBA_CENTRAL" remove={remove} />}
              </div>
            )}
          </Step>
          <p className="text-xs text-slate-500">
            Edvora signs in with the client credentials grant at <code>{saved.tokenUrl}</code> and uses the two-hour token it gets back. Central allows ten API
            calls a second for the whole account, so a sync asks for its pages slowly.
          </p>
        </>
      )}
    </div>
  );
}

/** What to import, and what to keep in step with it. */
export function ArubaSettings({ integration }: { integration: ArubaIntegration }) {
  const { can } = useAuth();
  const isAdmin = can('ADMIN');
  const plan = usePlan();
  const { update } = useIntegrationMutations('ARUBA_CENTRAL');
  const settings = integration.settings;
  const set = (patch: Partial<typeof settings>) => update.mutate({ settings: { ...settings, ...patch } });
  const kinds = settings.kinds ?? [];

  return (
    <div className="max-w-2xl space-y-6">
      <section className="space-y-3">
        <h3 className="text-sm font-semibold text-slate-900">What to import</h3>
        <p className="text-sm text-slate-500">Equipment in your Central account becomes an asset in Edvora, with its model, serial number, site and status.</p>
        {KINDS.map((kind) => (
          <Switch
            key={kind.value}
            checked={kinds.includes(kind.value)}
            disabled={!isAdmin || (kinds.length === 1 && kinds.includes(kind.value))}
            onChange={(on) => set({ kinds: on ? [...kinds, kind.value] : kinds.filter((value) => value !== kind.value) })}
            label={kind.label}
            description={kind.hint}
          />
        ))}
      </section>

      <section className="space-y-3">
        <h3 className="text-sm font-semibold text-slate-900">Assets</h3>
        <Switch
          checked={settings.autoLinkBySerial}
          disabled={!isAdmin}
          onChange={(autoLinkBySerial) => set({ autoLinkBySerial })}
          label="Match to assets by serial number"
          description="Equipment already in your inventory is linked to what Central knows about it."
        />
        <Switch
          checked={settings.autoCreateAssets}
          disabled={!isAdmin}
          onChange={(autoCreateAssets) => set({ autoCreateAssets })}
          label="Create assets for equipment that isn’t in Edvora yet"
          description="Each one gets an asset tag, and the category named Access Points, Switches, Gateways or Network Equipment when you have one."
        />
      </section>

      <section className="space-y-3">
        <h3 className="text-sm font-semibold text-slate-900">Chromebook location</h3>
        <Switch
          checked={settings.syncBssids}
          disabled={!isAdmin || !plan.has('DEVICE_LOCATION')}
          onChange={(syncBssids) => set({ syncBssids })}
          label="Keep the access point list in step with Central"
          description="Every BSSID your access points broadcast is added to Chromebook location, placed in the building and room of that access point’s asset. Move the asset and its Chromebooks move with it."
        />
        {!plan.has('DEVICE_LOCATION') && (
          <Alert tone="blue">
            This needs the Chromebook Location &amp; Recovery add-on. Until it’s in your plan, nothing about BSSIDs is read from Central.
          </Alert>
        )}
      </section>
    </div>
  );
}
